import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'react-final-form';
import { Form } from 'react-final-form';
import { Container, Row, Col } from 'react-bootstrap';
import { decamelizeKeys } from 'humps';
import cx from 'classnames';

import Step from './Step';
import Input from '../Forms/Input';
import RadioButtonGroup from '../Forms/RadioButtonGroup';
import API from '../../services/API';
import Button from '../UI/Button';
import Tabs from '../UI/Tabs';
import * as formValidators from '../../utils/formValidation';
import { withTranslation } from '../../i18n';
import styles from './styles.css';

const studyCities = [
  'antwerp',
  'brugge',
  'brussels',
  'charleroi',
  'ghent',
  'hasselt',
  'kortrijk',
  'leuven',
  'louvain_la_neuve',
  'luik',
  'mons',
  'namur',
];

class StepOccupations extends PureComponent {
  state = {
    isSubmitting: false,
  };

  handleSubmit = async formValues => {
    this.setState({ isSubmitting: true });

    const updatedServiceProfile = await API.fetch(
      undefined,
      '/user/service-profile-occupations/update',
      decamelizeKeys(formValues),
    );

    this.props.goToNextStep();
    this.setState({ isSubmitting: false });
    this.props.setServiceProfile(updatedServiceProfile);
  };

  getInitialValues() {
    const { serviceProfile } = this.props;
    const defaults = {
      studiesCity: 'ghent',
      studiesFinishYear: new Date().getFullYear(),
    };

    if (!serviceProfile) {
      return defaults;
    }

    return {
      ...serviceProfile,
      ...{
        studiesFinishYear: serviceProfile.studiesFinishYear || defaults.studiesFinishYear,
        studiesCity: serviceProfile.studiesCity || defaults.studiesCity,
      },
    };
  }

  validateForm = formValues => {
    const { t, serviceProfile } = this.props;
    const isMediaCrew = serviceProfile.crewType === 'media_crew';
    const errors = {};

    formValidators.required(formValues, errors, 'currentOccupation');

    if (!isMediaCrew) {
      formValidators.required(formValues, errors, 'studies');
      formValidators.required(formValues, errors, 'studiesCity');
      formValidators.required(formValues, errors, 'studiesFinishYear');
      formValidators.required(formValues, errors, 'futureFunction');
    }

    return errors;
  };

  render() {
    const { visible, goToPreviousStep, serviceProfile, t } = this.props;
    const { isSubmitting } = this.state;
    const isMediaCrew = serviceProfile.crewType === 'media_crew';

    return (
      <Step visible={visible}>
        <Form
          onSubmit={this.handleSubmit}
          initialValues={this.getInitialValues()}
          validate={this.validateForm}
          render={({ handleSubmit, pristine, invalid }) => (
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <Field
                  component={Input}
                  name="currentOccupation"
                  label={t('yourCurrentOccupation')}
                  as="select"
                  required
                >
                  <option value=""></option>
                  <option value="student">{t('occupations.student')}</option>
                  <option value="employed">{t('occupations.employed')}</option>
                  <option value="unemployed">{t('occupations.unemployed')}</option>
                  <option value="self_employed">{t('occupations.selfEmployed')}</option>
                </Field>
              </div>

              {!isMediaCrew && (
                <>
                  <div className="mb-3">
                    <Field component={Input} name="studies" label={t('yourStudies')} required />
                  </div>
                  <div className="mb-3">
                    <Field name="studiesCity" component={Input} as="select" label={t('studyCity')} required>
                      {studyCities.map(studyCity => (
                        <option key={studyCity} value={studyCity}>
                          {t(`cities.${studyCity}`)}
                        </option>
                      ))}
                    </Field>
                  </div>
                  <div className="mb-3">
                    <Field
                      component={Input}
                      name="studiesFinishYear"
                      type="number"
                      label={t('finishStudies')}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <Field
                      component={RadioButtonGroup}
                      name="futureFunction"
                      label={t('yourFutureFunction')}
                      required
                      options={[
                        { value: 'service_member', label: t('futureFunction.serviceMember') },
                        { value: 'team_lead', label: t('futureFunction.teamLead') },
                        { value: 'project_manager', label: t('futureFunction.projectManager') },
                      ]}
                    />
                  </div>
                </>
              )}

              {isMediaCrew && (
                <>
                  <div className="mb-3">
                    <Field component={Input} name="mediaInstagramProfile" label={t('mediaInstagramProfile')} />
                  </div>
                  <div className="mb-3">
                    <Field component={Input} name="mediaFacebookPage" label={t('mediaFacebookPage')} />
                  </div>
                  <div className="mb-3">
                    <Field component={Input} name="mediaPortfolio" label={t('mediaPortfolio')} />
                  </div>
                  <div className="mb-3">
                    <Field
                      component={Input}
                      name="mediaTripType"
                      label={t('mediaTripType')}
                      helpText={`(${t('mediaTripTypeHelpText')})`}
                    />
                  </div>
                </>
              )}

              <div className={cx('mt-5', styles.formButtons)}>
                <Button type="button" level="secondary" onClick={goToPreviousStep} className="mr-3">
                  {t('back')}
                </Button>
                <Button type="submit" processing={isSubmitting}>
                  {t('next')}
                </Button>
              </div>
            </form>
          )}
        />
      </Step>
    );
  }
}

StepOccupations.propTypes = {
  visible: PropTypes.bool,
  t: PropTypes.func.isRequired,
};

export default withTranslation('common')(StepOccupations);
